/**
 * RFC 0289 — answering a comment, and telling the session that wrote it.
 *
 * The READING half of threading (RFC 0289 S1) gave a comment a `parentId` and a
 * projection that groups by it. This is the DELIVERY half: a reply is written,
 * and the session whose comment was answered hears about it.
 *
 * RFC 0289 §2 lists notification routing as a NON-GOAL, deferred to RFC 0283.
 * It is delivered here anyway, and the reason is §1's own complaint: an agent's
 * comment that nobody can answer back to is what the RFC set out to fix, and
 * threading without this produces a thread the agent cannot hear. What §2 ruled
 * out is mentions and a routing table; this is neither. It is the one edge
 * threading already implies — a reply has a parent, the parent has an author,
 * and that author is a session.
 *
 * WHY THIS IS NOT `mutations/comments.ts#replyToComment`. That is a write and
 * nothing else, and it stays that way: waking a session is a policy decision
 * with a queued inference run behind it, and folding the two together would mean
 * every caller that happens to write a reply also queues turns. This composes
 * over it rather than reimplementing it, so there is exactly one place that
 * knows how a reply attaches (root normalisation, target inheritance) and
 * exactly one that knows who gets woken.
 *
 * WHY ONE FUNCTION AND NOT A HOOK PER SURFACE. Three surfaces create replies:
 * the agent's `comment` tool, the sandboxed app bridge, and the host-realm apps.
 * A notification rule written three times is a rule that holds in two of them,
 * and the failure is silent — the person replies, nothing happens, and there is
 * nothing on screen to say a message was dropped.
 *
 * THE POINTER, NOT THE PROSE. The reply stays on the document where it is
 * anchored and where resolving or deleting it means something. The session gets
 * an event carrying the thread id and the first line, and answers with the
 * `comment` tool. Copying reply bodies into the transcript would put half of
 * every exchange somewhere it cannot be retracted from.
 */

import type { Repo } from '@automerge/automerge-repo/slim'
import { replyToComment } from '../../model/comments/mutations'
import { projectComments } from '../../model/comments/reads'
import { locateComment, mergeComments } from '../../model/comments/store'
import type { CommentEntry, Mention } from '../../model/comments/types'
import { projectAuthor } from '../../model/people/reads'
import type { ProjectDoc } from '../../model/project/types'
import { enqueueCommentReplyNotification } from '../run/enqueue-run'

/** How much of the reply rides along on the log. One line, per
 *  `SessionRepliedData.summary`: the thread is a `comment action:list` away. */
const SUMMARY_MAX = 280

export interface AnswerCommentResult {
  /** The new comment. */
  id: string
  /** The session told about it, or null when nobody was. */
  notifiedSessionId: string | null
  /**
   * Why nobody was told, when nobody was. Returned rather than logged because
   * the surfaces differ in what they should do about it: a UI can stay silent,
   * while a test wants to assert that a reply to a person notified no one.
   *
   * There is no reason for an unknown parent: `replyToComment` THROWS on one
   * (RFC 0289 §3), so a reply that cannot attach never becomes a root nobody
   * hears about — it fails where the person typing can be told.
   */
  reason?: 'no-author-session' | 'self-reply' | 'captured-session'
}

export async function answerComment(
  repo: Repo,
  projectId: string,
  opts: {
    /** The comment being answered. May be a root or another reply. */
    parentId: string
    author: string
    body: string
    /** The session writing the reply, when an agent is. Used both to record
     *  provenance and to avoid waking itself. */
    authorSessionId?: string
    /** What the reply NAMES (RFC 0292). Stored on it; waking the sessions named
     *  is `deliverMentions`, a separate act with its own guards, so a reply that
     *  names somebody does not queue two runs from one function. */
    mentions?: Mention[]
  },
): Promise<AnswerCommentResult> {
  const handle = await repo.find<ProjectDoc>(projectId as never)
  await (handle as { whenReady?: () => Promise<void> }).whenReady?.()

  // Read the DIRECT parent BEFORE writing, because `replyToComment` normalises
  // the new comment's `parentId` to the root and the direct parent is who you
  // are answering. In a thread where an agent answered a person's comment, a
  // further reply is aimed at the agent, and only the direct parent says so.
  // The thread is read where it lives (RFC 0339): the parent's comments
  // document, plus anything still on the project.
  const home = await locateComment(repo, projectId, opts.parentId)
  const before = projectComments(
    home?.kind === 'comments'
      ? mergeComments(handle.doc(), [home.handle.doc()])
      : handle.doc(),
  )
  const byId = new Map(before.map((c) => [c.id, c]))
  const parent = byId.get(opts.parentId)

  // Throws on an unknown parent. Deliberately not caught: see `reason`.
  const id = await replyToComment(repo, projectId, {
    parentId: opts.parentId,
    author: opts.author,
    body: opts.body,
    ...(opts.authorSessionId ? { authorSessionId: opts.authorSessionId } : {}),
    ...(opts.mentions?.length ? { mentions: opts.mentions } : {}),
  })

  const notify = whoToNotify(parent, byId, opts.authorSessionId)
  if (!notify.sessionId) return { id, notifiedSessionId: null, ...notify.why }

  const root = parent?.parentId ? byId.get(parent.parentId) : parent
  const delivered = await enqueueCommentReplyNotification(
    repo,
    notify.sessionId,
    {
      commentId: id,
      // What to pass back as `parentId` to answer. The root, because that is
      // the thread; replying to a reply lands in the same place regardless.
      parentId: root?.id ?? opts.parentId,
      documentId: root?.target?.documentId ?? '',
      // The writer's NAME, not their id: this is the line an agent reads.
      author: projectAuthor(handle.doc(), {
        author: opts.author,
        ...(opts.authorSessionId
          ? { authorSessionId: opts.authorSessionId }
          : {}),
      }).label,
      anchorText: root?.target?.anchorText ?? '',
      nodeId: root?.target?.nodeId ?? '',
      summary: summarise(opts.body),
    },
    { projectId },
  )
  return delivered
    ? { id, notifiedSessionId: notify.sessionId }
    : { id, notifiedSessionId: null, reason: 'captured-session' }
}

/**
 * Which session hears about this reply, if any.
 *
 * The DIRECT parent's session first, falling back to the root's: you are
 * answering somebody, and in a thread with two agents in it the one you replied
 * to is the one that should answer. Only one session is told, so a long thread
 * does not queue a turn on every agent that ever spoke in it.
 */
function whoToNotify(
  parent: CommentEntry | undefined,
  byId: Map<string, CommentEntry>,
  replierSessionId: string | undefined,
): {
  sessionId: string | null
  why: { reason?: AnswerCommentResult['reason'] }
} {
  // Unreachable in practice — an absent parent means `replyToComment` threw
  // before we got here — but the type says it can be undefined, and a guess
  // about who to wake is worse than waking nobody.
  if (!parent) return { sessionId: null, why: { reason: 'no-author-session' } }
  const root = parent.parentId ? byId.get(parent.parentId) : parent
  const sessionId =
    parent.authorSessionId ??
    root?.authorSessionId ??
    readerOf(root, byId) ??
    null
  if (!sessionId) {
    // A thread between people. Nothing to wake, and nothing wrong.
    return { sessionId: null, why: { reason: 'no-author-session' } }
  }
  if (sessionId === replierSessionId) {
    // A session answering itself in its own thread. Queuing a run here is a
    // loop: the turn would see a reply it wrote and be free to reply again.
    return { sessionId: null, why: { reason: 'self-reply' } }
  }
  return { sessionId, why: {} }
}

/**
 * The session this thread is with: the last one to answer in it or to read
 * it without answering (RFC 0324 3g).
 *
 * A person's thread has no author session at its root, so the rule above
 * finds nobody. But an agent that replied in it, or read it and passed, is
 * the session the thread is with, and a follow-up must reach it, or the
 * person asks into silence. The latest one wins: a thread handed from one
 * agent to another goes on with the second.
 */
function readerOf(
  root: CommentEntry | undefined,
  byId: Map<string, CommentEntry>,
): string | null {
  if (!root) return null
  let best: { id: string; at: number } | null = null
  const consider = (id: string, at: number) => {
    if (!best || at > best.at) best = { id, at }
  }
  for (const c of byId.values()) {
    if (c.id !== root.id && c.parentId !== root.id) continue
    if (c.authorSessionId) consider(c.authorSessionId, c.createdAt ?? 0)
    for (const [id, at] of Object.entries(c.readBy ?? {})) consider(id, at)
  }
  return (best as { id: string } | null)?.id ?? null
}

/**
 * The session a reply to `parentId` would wake, or null (RFC 0324 3c).
 *
 * The same rule `answerComment` delivers by, exposed so a comment box can say
 * who will read a reply BEFORE it is sent. One rule, read in two places, so the
 * line under the box and the delivery cannot disagree.
 */
export function replyWakes(
  comments: CommentEntry[],
  parentId: string,
  replierSessionId?: string,
): string | null {
  const byId = new Map(comments.map((c) => [c.id, c]))
  return whoToNotify(byId.get(parentId), byId, replierSessionId).sessionId
}

/** First line, bounded. A reply is read in the thread; this is the nudge. */
function summarise(body: string): string {
  const line = body.trim().split('\n', 1)[0] ?? ''
  return line.length > SUMMARY_MAX ? `${line.slice(0, SUMMARY_MAX - 1)}…` : line
}
